import React, { createContext, useContext, useMemo } from "react";
import { tr } from "./tr";
import { en } from "./en";

export type Locale = "tr" | "en";
export type Translations = Record<string, string>;

function detectLocale(): Locale {
  if (typeof window !== "undefined") {
    const pathname = window.location.pathname;
    if (pathname.startsWith("/en") && (pathname.length === 3 || pathname[3] === "/")) {
      return "en";
    }
  }
  return "tr";
}

const translations: Record<Locale, Translations> = { tr, en };

interface LocaleContextValue {
  locale: Locale;
  t: (key: string) => string;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: "tr",
  t: (key: string) => tr[key] ?? key,
});

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const value = useMemo<LocaleContextValue>(() => {
    const locale = detectLocale();
    const dict = translations[locale];
    return {
      locale,
      t: (key: string) => dict[key] ?? key,
    };
  }, []);

  return React.createElement(LocaleContext.Provider, { value }, children);
};

export const useLocale = (): LocaleContextValue => useContext(LocaleContext);

/** For non-component code (lib files, services, etc.) */
export function getT(): (key: string) => string {
  const locale = detectLocale();
  const dict = translations[locale];
  return (key: string) => dict[key] ?? key;
}
